import React from "react";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import RestaurantTableLayoutEditor from "../../components/dashboard/tables/RestaurantTableLayoutEditor";
import RestaurantTableOperationsView from "../../components/dashboard/tables/RestaurantTableOperationsView";
import TableReservationsPanel from "../../components/dashboard/reservations/TableReservationsPanel";

const BRAND = { dark: "#19242F" };

function capabilityEnabled(project, capability, legacyModule) {
  return Boolean(project && (
    project?.capabilities?.[capability] === true ||
    project?.modules?.[legacyModule] === true ||
    isProjectModuleEnabled(project, legacyModule)
  ));
}

export default function ProjectTablesLayoutPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [tab, setTab] = React.useState("operation");

  React.useEffect(() => { loadProjects(); }, [loadProjects]);
  const project = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  React.useEffect(() => {
    const found = projects.find((item) => item.slug === projectId || item.id === projectId);
    if (found && found !== activeProject) setActiveProject(found);
  }, [projectId, projects, activeProject, setActiveProject]);

  const tablesEnabled = capabilityEnabled(project, "tables", "tables");
  const reservationsEnabled = capabilityEnabled(project, "tableReservations", "table_reservations");
  const operationsEnabled = tablesEnabled && reservationsEnabled;

  React.useEffect(() => {
    if (operationsEnabled) return;
    if (tablesEnabled) setTab("layout");
    else if (reservationsEnabled) setTab("reservations");
  }, [tablesEnabled, reservationsEnabled, operationsEnabled]);

  React.useEffect(() => {
    const action = String(searchParams.get("accion") || "").trim().toLowerCase();
    if (action === "crear-mesa" && tablesEnabled) setTab("layout");
  }, [searchParams, tablesEnabled]);

  if (!project) return null;
  if (!tablesEnabled && !reservationsEnabled) {
    return <ModuleDisabledState projectId={projectId} title="Mesas" description="Activá Mesas o Reservas de mesa para usar la operación gastronómica." />;
  }

  const visibleTabs = [
    operationsEnabled && ["operation", <GridViewRoundedIcon />, "Operación"],
    tablesEnabled && ["layout", <TableRestaurantRoundedIcon />, "Editar plano"],
    reservationsEnabled && ["reservations", <EventSeatRoundedIcon />, "Reservas gastronómicas"],
  ].filter(Boolean);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>Mesas</Typography>
        <Typography sx={{ mt: .5, color: "rgba(25,36,47,.65)" }}>{project?.name || projectId} · operación gastronómica separada de la agenda de servicios</Typography>
      </Box>

      {visibleTabs.length > 1 ? (
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map(([value, icon, label]) => <Tab key={value} value={value} icon={icon} iconPosition="start" label={label} />)}
        </Tabs>
      ) : null}

      {operationsEnabled && tab === "operation" ? <RestaurantTableOperationsView projectId={projectId} /> : null}
      {tablesEnabled && tab === "layout" ? <RestaurantTableLayoutEditor projectId={projectId} /> : null}
      {reservationsEnabled && tab === "reservations" ? <TableReservationsPanel projectId={projectId} /> : null}
    </Stack>
  );
}
